import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { useMemo } from 'react'
import * as yup from 'yup'
import { useTranslations } from '@/lib/next-shim'

// A minimal translator type: pass-through (key === message) when no `t` is supplied,
// so the schema stays usable for type inference without React context.
type Translate = (key: string) => string
const identity: Translate = (key) => key

export function buildRegisterSchema(t: Translate = identity) {
  return yup.object({
    email: yup.string().email(t('validation.email.invalid')).required(t('validation.email.required')),
    name: yup.string().required(t('validation.firstName.required')),
    establishment: yup.string().optional(),
    phone: yup.string().optional(),
    secu: yup
      .string()
      .transform((val) => (val === '' ? null : val))
      .nullable()
      .transform((value) => value || undefined)
      .optional(),

    surname: yup.string().required(t('validation.lastName.required')),
    // INS Fields
    birthName: yup.string().required(t('validation.birthName.required')),
    birthFirstNames: yup.string().required(t('validation.birthFirstNames.required')),
    birthDate: yup.string().required(t('validation.birthDate.required')),
    sex: yup.string().oneOf(['M', 'F']).required(t('validation.sex.required')),
    birthPlace: yup.string().required(t('validation.birthPlace.required')),
    terms: yup.boolean().oneOf([true], t('validation.terms.required')).required(t('validation.terms.required')),
    password: yup.string().required(t('validation.password.required')).min(8, t('validation.password.min')),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref('password')], t('validation.password.mustMatch'))
      .required(t('validation.confirmPassword.required')),
  })
}

// Schema with key-as-message defaults — drives the form-data type.
const formSchema = buildRegisterSchema()

export type DefaultRegisterFormData = yup.InferType<typeof formSchema>

export function useDefaultRegisterForm() {
  const t = useTranslations()
  const schema = useMemo(() => buildRegisterSchema(t), [t])
  return useForm<DefaultRegisterFormData>({
    defaultValues: {
      email: '',
      name: '',
      establishment: '',
      phone: '',
      secu: '',
      surname: '',
      birthName: '',
      birthFirstNames: '',
      birthDate: '',
      sex: 'M',
      birthPlace: '',
      terms: false,
      password: '',
      confirmPassword: '',
    },
    mode: 'onChange',
    resolver: yupResolver(schema),
  })
}
